import loadable from '@loadable/component';
import { Redirect, Switch } from 'react-router';

import DocumentLayout from '@app/components/DocumentLayout';
import EditorLayout from '@app/components/EditorLayout';
import MainLayout from '@app/components/MainLayout';
import ParticipantLayout from '@app/components/ParticipantLayout';
import PaymentLayout from '@app/components/PaymentLayout';
import RootLayout from '@app/components/RootLayout';
import Route from '@app/components/Route';
import Authentication from '@app/containers/Authentication';
import AuthLayout from '@app/containers/AuthLayout';
import PasswordLayout from '@app/containers/PasswordLayout';
import SettingLayout from '@app/containers/SettingLayout';
import SigningLayout from '@app/containers/SigningLayout';
import { ContractLinkCreateLayout } from '@app/features/contractLink/contractLinkCreate';
import { FileHashPageLayout } from '@app/features/fileHash';
import { TemplateCreationLayout } from '@app/features/template/components/TemplateCreationLayout';
import TemplateEditionLayout from '@app/features/template/components/TemplateEditionLayout';
import { settingsWorkspaceRoutes } from '@app/features/workspaceManagement/routes';
import useDataLayer from '@app/hooks/useDataLayer';
import { ExitPathContextProvider } from '@app/hooks/useExitPathContext';
import useOnboardingGuide from '@app/hooks/useOnboardingGuide';
import embeddedService from '@app/libs/embedded/embeddedService';
import InvitationVerificationPage from '@app/routes/Authentication/InvitationVerification';
import KakaoCallbackPage from '@app/routes/Authentication/KakaoCallback';
import DocumentIdPage from '@app/routes/Document/DocumentId';
import DocumentDownloadPage from '@app/routes/DocumentDownload';
import EmbeddedDraftPreviewPage from '@app/routes/Draft/Preview';
import EmbeddedDocumentPage from '@app/routes/EmbeddedDocument/DocumentId';
import EmbeddedDraftPage from '@app/routes/EmbeddedDraft/DraftId';
import EmbeddedTemplatePage from '@app/routes/EmbeddedTemplate/TemplateId';
import NotFoundPage from '@app/routes/NotFound';
import VirtualAccountCreateSuccessPage from '@app/routes/Payment/VirtualAccountCreateSuccess';
import SignOutPage from '@app/routes/SignOut';

const HomePage = loadable(() => import(/* webpackChunkName: "home" */ 'routes/Home'));

const ExternalServicePage = loadable(() =>
  import(/* webpackChunkName: "external-service" */ 'routes/Settings/ExternalService')
);

const BulkRequestPage = loadable(() =>
  import(/* webpackChunkName: "bulk-request" */ 'routes/BulkRequest')
);

const BulkRequestPreviewPage = loadable(() =>
  import(/* webpackChunkName: "bulk-request_preview" */ 'routes/BulkRequest/Preview')
);

const BulkRequestTemplatePage = loadable(() =>
  import(/* webpackChunkName: "bulk-request_template" */ 'routes/BulkRequest/Template')
);

const BulkRequestsPage = loadable(() =>
  import(/* webpackChunkName: "bulk-requests" */ 'routes/BulkRequests')
);

const DraftsPage = loadable(() => import(/* webpackChunkName: "forms" */ 'routes/Drafts'));

const EmailVerificationForApiPage = loadable(() =>
  import(
    /* webpackChunkName: "authentication_email-verification-for-api" */ 'routes/Authentication/EmailVerificationForApi'
  )
);

const ExternalDocumentsBatchUpdateHistoryPage = loadable(() =>
  import(
    /* webpackChunkName: "external-documents-batch-update" */ 'routes/ExternalDocuments/BatchUpdate'
  )
);

const ExternalDocumentsDocumentListPage = loadable(() =>
  import(/* webpackChunkName: "external-documents-document-list" */ 'routes/ExternalDocuments')
);

const ExternalDocumentsDocumentDetailPage = loadable(() =>
  import(
    /* webpackChunkName: "external-documents-document-detail" */ 'routes/ExternalDocuments/Document/DocumentId'
  )
);

const ExternalDocumentsUploadPage = loadable(() =>
  import(/* webpackChunkName: "external-documents-upload" */ 'routes/ExternalDocuments/Upload')
);

const ExternalDocumentsFileViewerPage = loadable(() =>
  import(
    /* webpackChunkName: "external-documents-file-viewer" */ 'routes/ExternalDocuments/FileViewer'
  )
);

const ExportInputValuesDownloadPage = loadable(() =>
  import(
    /* webpackChunkName: "export-input-values-download" */ 'routes/ExportInput/Values/Download'
  )
);

const FacebookCallbackPage = loadable(() =>
  import(
    /* webpackChunkName: "authentication/facebook-callback" */ 'routes/Authentication/FacebookCallback'
  )
);

const InactivePage = loadable(() =>
  import(/* webpackChunkName: "authentication_inactive" */ 'routes/Authentication/Inactive')
);

const LinksPage = loadable(() => import(/* webpackChunkName: "links" */ 'routes/Links'));
const LinksCreatePositionPage = loadable(() =>
  import(/* webpackChunkName: "links_create_position" */ 'routes/Links/Create/Position')
);
const LinksCreateEtcPage = loadable(() =>
  import(/* webpackChunkName: "links_create_etc" */ 'routes/Links/Create/Etc')
);
const LinksLinkIdPage = loadable(() =>
  import(/* webpackChunkName: "links_linkId" */ 'routes/Links/LinkId')
);

const LinksAuthenticationPage = loadable(() =>
  import(/* webpackChunkName: "links_authentication" */ 'routes/Links/Authentication')
);

const LinksTemplatePage = loadable(() =>
  import(/* webpackChunkName: "links_template" */ 'routes/Links/Template')
);

const ApprovalListPage = loadable(() =>
  import(/* webpackChunkName: "approvals_approvalList" */ 'routes/Approvals/ApprovalList')
);

const ReminderListPage = loadable(() =>
  import(/* webpackChunkName: "reminders_reminderList" */ 'routes/Reminders/ReminderList')
);

const ReminderListSamplePage = loadable(() =>
  import(
    /* webpackChunkName: "reminders_reminderList_sample" */ '@app/routes/Reminders/ReminderListSample'
  )
);

const ApprovalsEditPage = loadable(() =>
  import(/* webpackChunkName: "approvals_edit" */ 'routes/Approvals/Edit')
);

const ApprovalsApprovalIdPage = loadable(() =>
  import(/* webpackChunkName: "approvals_approvalId" */ 'routes/Approvals/ApprovalId')
);

const ApprovalDocumentPreview = loadable(() =>
  import(/* webpackChunkName: "approval_document_preview" */ 'routes/Approvals/ApprovalId/Preview')
);

const NaverCallbackPage = loadable(() =>
  import(
    /* webpackChunkName: "authentication_naver-callback" */ 'routes/Authentication/NaverCallback'
  )
);
const SigninPage = loadable(() =>
  import(/* webpackChunkName: "authentication_signin" */ 'routes/Authentication/SignIn')
);
const SignUpCheckPage = loadable(() =>
  import(/* webpackChunkName: "authentication_signupcheck" */ 'routes/Authentication/SignUpCheck')
);
const SignUpCheckSuccessPage = loadable(() =>
  import(
    /* webpackChunkName: "authentication_signupcheck_success" */ 'routes/Authentication/SignUpCheckSuccess'
  )
);
const SignUpCheckTokenPage = loadable(() =>
  import(
    /* webpackChunkName: "authentication_email-verification" */ 'routes/Authentication/SignUpCheckToken'
  )
);
const SignUpPage = loadable(() =>
  import(/* webpackChunkName: "authentication_signup" */ 'routes/Authentication/SignUp')
);
const SsoPage = loadable(() =>
  import(/* webpackChunkName: "authentication_sso" */ 'routes/Authentication/Sso/SignIn')
);
const SsoCallbackPage = loadable(() =>
  import(/* webpackChunkName: "authentication_sso" */ 'routes/Authentication/Sso/Callback')
);

const SsoSignUpPage = loadable(() =>
  import(/* webpackChunkName: "authentication_sso" */ 'routes/Authentication/Sso/SignUp')
);

const TermsPage = loadable(() =>
  import(/* webpackChunkName: "authentication_terms" */ 'routes/Authentication/Terms')
);
const AbortedPage = loadable(() =>
  import(/* webpackChunkName: "document_aborted" */ 'routes/Document/Aborted')
);
const AuditTrailPage = loadable(() =>
  import(/* webpackChunkName: "document_audit-trail" */ 'routes/Document/AuditTrail')
);
const CompletedPage = loadable(() =>
  import(/* webpackChunkName: "document_completed" */ 'routes/Document/Completed')
);
const DocumentViewerPage = loadable(() =>
  import(/* webpackChunkName: "document_viewer" */ 'routes/Document/DocumentViewer')
);
const DocumentBundleDownloadPage = loadable(() =>
  import(/* webpackChunkName: "document_bundle_download" */ 'routes/DocumentBundle/Download')
);
const DraftDraftIdPage = loadable(() =>
  import(/* webpackChunkName: "draft_draftId" */ 'routes/Draft/DraftId')
);
const DraftPreviewPage = loadable(() =>
  import(/* webpackChunkName: "draft_preview" */ 'routes/DraftPreview')
);
const DraftTempPage = loadable(() =>
  import(/* webpackChunkName: "draft_draftId" */ 'routes/Draft/Temp')
);
const OnSigningPage = loadable(() =>
  import(/* webpackChunkName: "on-signing" */ 'routes/Document/OnSigning')
);
const ScheduledDocumentPage = loadable(() =>
  import(/* webpackChunkName: "document_scheduled" */ 'routes/Document/Scheduled')
);
const WaitForSigningPage = loadable(() =>
  import(/* webpackChunkName: "document_wait-for-signing" */ 'routes/Document/WaitForSigning')
);
const DocumentsPage = loadable(() =>
  import(/* webpackChunkName: "documents" */ 'routes/Documents')
);
const DocumentDetailPage = loadable(() =>
  import(/* webpackChunkName: "documents" */ 'routes/DocumentDetail')
);
const DocumentDetailSamplePage = loadable(() =>
  import(/* webpackChunkNAme: "document_detail_sample" */ 'routes/DocumentDetailSample')
);

const EditorOthersRequireRoute = loadable(() =>
  import(/* webpackChunkName: "editor_others-require" */ 'routes/Editor/OthersRequire')
);
const EditorSinglePage = loadable(() =>
  import(/* webpackChunkName: "editor_single" */ 'routes/Editor/Single')
);
const EditorValidationPage = loadable(() =>
  import(/* webpackChunkName: "editor_validation" */ 'routes/Editor/Validation')
);
const EditingDocumentsPage = loadable(() =>
  import(/* webpackChunkName: "editing-documents" */ 'routes/EditingDocuments')
);
const EmbeddedParticipantPage = loadable(() =>
  import(/* webpackChunkName: "embedded-participant" */ 'routes/EmbeddedParticipant')
);
const ParticipantPage = loadable(() =>
  import(/* webpackChunkName: "participant" */ 'routes/Participant')
);

const AccessWithNamePage = loadable(() =>
  import(/* webpackChunkName: "participant_access-with-name" */ 'routes/Participant/AccessWithName')
);

const ForgotPage = loadable(() =>
  import(/* webpackChunkName: "password_forgot" */ 'routes/Password/Forgot')
);
const FormAgreementPage = loadable(() =>
  import(/* webpackChunkName: "form_agreement" */ 'routes/FormAgreement')
);
const FormPreviewPage = loadable(() =>
  import(/* webpackChunkName: "form_preview" */ 'routes/FormPreview/FormId')
);
const ResetPage = loadable(() =>
  import(/* webpackChunkName: "password_reset" */ 'routes/Password/Reset')
);
const ResetInvalidPage = loadable(() =>
  import(/* webpackChunkName: "password_reset_invalid" */ 'routes/Password/ResetInvalid')
);
const ProductPage = loadable(() =>
  import(/* webpackChunkName: "payment_plan-change" */ 'routes/Payment/Product')
);
const PlanChangePage = loadable(() =>
  import(/* webpackChunkName: "payment_plan-change" */ 'routes/Payment/PlanChange')
);
const ReadySubscriptionPage = loadable(() =>
  import(/* webpackChunkName: "payment_ready_subscription" */ 'routes/Payment/ReadySubscription')
);
const SeatPage = loadable(() =>
  import(/* webpackChunkName: "payment_seat_subscription" */ 'routes/Payment/Seat')
);
const SubscriptionPage = loadable(() =>
  import(/* webpackChunkName: "payment_subscription" */ 'routes/Payment/Subscription')
);
const AddressBookPage = loadable(() =>
  import(/* webpackChunkName: "settings_address_book" */ 'routes/Settings/AddressBook')
);
const AddressBookExcelUploadPage = loadable(() =>
  import(
    /* webpackChunkName: "settings_address_book_excel_upload" */ 'routes/Settings/AddressBook/ExcelUpload'
  )
);

const AlarmPage = loadable(() => import('routes/Settings/Alarm'));

const ApiPage = loadable(() =>
  import(/* webpackChunkName: "settings_api" */ 'routes/Settings/Api')
);
const ApiKeyPage = loadable(() =>
  import(/* webpackChunkName: "settings_apikey" */ 'routes/Settings/ApiKey')
);
const ApplicationPage = loadable(() =>
  import(/* webpackChunkName: "settings_application" */ 'routes/Settings/Application')
);
const ApplicationsPage = loadable(() =>
  import(/* webpackChunkName: "settings_applications" */ 'routes/Settings/Applications')
);
const BillingPage = loadable(() =>
  import(/* webpackChunkName: "settings_billing" */ 'routes/Settings/Billing')
);
const CustomBrandingPage = loadable(() =>
  import(/* webpackChunkName: "settings_custom-branding" */ 'routes/Settings/CustomBranding')
);

const WorkspaceSettingPage = loadable(() =>
  import(
    /* webpackChunkName: "settings_workspace_workspace" */ '@app/routes/Settings/Workspace/Workspace'
  )
);
const WorkspaceAutomaticSharingPage = loadable(() =>
  import(
    /* webpackChunkName: "settings_workspace_automatic-sharing" */ '@app/routes/Settings/Workspace/AutomaticSharing'
  )
);
const WorkspaceMemberInvitationPage = loadable(() =>
  import(
    /* webpackChunkName: "settings_workspace_member-invitation" */ '@app/routes/Settings/Workspace/MemberInvitation'
  )
);
const ChangeWorkspaceOwnerPage = loadable(() =>
  import(
    /* webpackChunkName: "settings_workspace_chage-owner" */ '@app/routes/Settings/Workspace/ChangeOwner'
  )
);
const DeleteWorkspacePage = loadable(() =>
  import(
    /* webpackChunkName: "settings_workspace_delete" */ '@app/routes/Settings/Workspace/Delete'
  )
);
const WorkspaceMembersPage = loadable(() =>
  // TODO(@highcastlee): import 경로 수정
  import(
    /* webpackChunkName: "settings_workspace_member" */ '@app/routes/Settings/Workspace/Members'
  )
);

const WorkspaceGroupsPage = loadable(() =>
  import(
    /* webpackChunkName: "settings_workspace_groups" */ '@app/routes/Settings/Workspace/Groups'
  )
);
const WorkspaceGroupDetailPage = loadable(() =>
  import(
    /* webpackChunkName: "settings_workspace_group_detail" */ '@app/routes/Settings/Workspace/GroupDetail'
  )
);
const PlanPage = loadable(() =>
  import(/* webpackChunkName: "settings_profile" */ 'routes/Settings/Plan')
);
const ProfilePage = loadable(() =>
  import(/* webpackChunkName: "settings_profile" */ 'routes/Settings/Profile')
);

const WorkspacePage = loadable(() =>
  import(/* webpackChunkName: "settings_workspace" */ 'routes/Settings/Workspace')
);

const WorkspaceRolePage = loadable(() =>
  import(
    /* webpackChunkName: "settings_workspace_role-form" */ '@app/routes/Settings/Workspace/RolePage'
  )
);

const WorkspaceRoleFormPage = loadable(() =>
  import(
    /* webpackChunkName: "settings_workspace_role-form" */ '@app/routes/Settings/Workspace/RolePage/RoleForm'
  )
);

const WebhookPage = loadable(() =>
  import(/* webpackChunkName: "settings_webhook" */ 'routes/Settings/Webhook')
);

const FileHashVerificationPage = loadable(() =>
  import(/* webpackChunkName: "file_hash_verification" */ 'routes/FileHash/Verification')
);

const SignaturesPage = loadable(() =>
  import(/* webpackChunkName: "signatures" */ 'routes/Signatures')
);
const SigningAuthenticationPage = loadable(() =>
  import(/* webpackChunkName: "signing_authentication" */ 'routes/Signing/Authentication')
);
const SigningCompletePage = loadable(() =>
  import(/* webpackChunkName: "signing_complete" */ 'routes/Signing/Complete')
);
const SigningExpiredPage = loadable(() =>
  import(/* webpackChunkName: "signing_expired" */ 'routes/Signing/Expired')
);
const ProgressPage = loadable(() =>
  import(/* webpackChunkName: "signing_progress" */ 'routes/Signing/Progress')
);
const TemplateSetupParticipantSetupPage = loadable(() =>
  import(
    /* webpackChunkName: "template_creation_participant_setup" */ 'routes/Template/Setup/ParticipantSetup'
  )
);
const TemplateSetupFieldSetupPage = loadable(() =>
  import(/* webpackChunkName: "template_creation_field_setup" */ 'routes/Template/Setup/FieldSetup')
);
const TemplateSetupGeneralSetupPage = loadable(() =>
  import(
    /* webpackChunkName: "template_creation_general_setup" */ 'routes/Template/Setup/GeneralSetup'
  )
);
const TemplateEditParticipantEditPage = loadable(() =>
  import(
    /* webpackChunkName: "template_edition_participant_edit" */ 'routes/Template/Edit/ParticipantEdit'
  )
);
const TemplateEditFieldEditPage = loadable(() =>
  import(/* webpackChunkName: "template_edition_field_edit" */ 'routes/Template/Edit/FieldEdit')
);
const TemplateEditGeneralEditPage = loadable(() =>
  import(/* webpackChunkName: "template_edition_general_edit" */ 'routes/Template/Edit/GeneralEdit')
);
const TemplatesPage = loadable(() =>
  import(/* webpackChunkName: "templates" */ 'routes/Templates')
);

const ViewerPage = loadable(() => import(/* webpackChunkName: "viewer" */ 'routes/Viewer'));

const PermissionAuthorizationPage = loadable(() =>
  import(
    /* webpackChunkName: "permission_authorization" */ 'routes/Authentication/PermissionAuthorization'
  )
);

const RedirectAuthorizationPage = loadable(() =>
  import(
    /* webpackChunkName: "redirect_authorization" */ 'routes/Authentication/RedirectAuthorization'
  )
);

const ExternalServiceConnectionErrorPage = loadable(() =>
  import(
    /* webpackChunkName: "external_service_connection_error" */ 'routes/Authentication/ExternalServiceConnectionError'
  )
);

const InPersonHostPage = loadable(() =>
  import(/* webpackChunkName: "in_person_host" */ 'routes/InPersonHost')
);

const InPersonSignerPage = loadable(() =>
  import(/* webpackChunkName: "in_person_signer" */ 'routes/InPersonSigner')
);

const InPersonWaitForSigning = loadable(() =>
  import(/* webpackChunkName: "in_person_wait_for_signing" */ 'routes/InPersonWaitForSigning')
);

function AppRoutes() {
  return (
    <RootLayout>
      <Switch>
        <Route
          exact
          path="/"
          render={(props) => (
            <MainLayout>
              <HomePage {...props} />
            </MainLayout>
          )}
        />
        <Route
          path="/authentication"
          render={(props) => (
            <Switch>
              <Route component={FacebookCallbackPage} path="/authentication/facebook-callback" />
              <Route component={NaverCallbackPage} path="/authentication/naver-callback" />
              <Route component={KakaoCallbackPage} path="/authentication/kakao-callback" />
              <AuthLayout {...props}>
                <Switch>
                  <Redirect exact path="/authentication" to="/documents" />
                  <Route
                    component={SignUpCheckTokenPage}
                    path="/authentication/email-verification/:token"
                  />
                  <Route
                    component={EmailVerificationForApiPage}
                    path="/authentication/email-verification-for-api/:token"
                  />
                  <Route component={InactivePage} path="/authentication/inactive" />
                  <Route
                    component={InvitationVerificationPage}
                    path="/authentication/invitation-verification/:invitationId"
                  />
                  <Route component={SigninPage} path="/authentication/signin" />
                  <Route component={SignUpPage} path="/authentication/signup" />
                  <Route component={SignUpCheckPage} exact path="/authentication/signupcheck" />
                  <Route
                    component={SignUpCheckSuccessPage}
                    path="/authentication/signupcheck/success"
                  />
                  <Route component={SsoCallbackPage} path="/authentication/sso/callback" />
                  <Route component={SsoSignUpPage} path="/authentication/sso/signup" />
                  <Route component={SsoPage} path="/authentication/sso/signin" />
                  <Route component={TermsPage} path="/authentication/terms" />
                </Switch>
              </AuthLayout>
            </Switch>
          )}
        />
        <Route component={SignOutPage} path="/signout" />
        <Route
          path="/bulk-request/:bulkRequestId/preview/:taskId"
          render={() => (
            <Authentication>
              <BulkRequestPreviewPage />
            </Authentication>
          )}
        />
        <Route
          path="/bulk-request/:bulkRequestId/template"
          render={() => (
            <Authentication>
              <BulkRequestTemplatePage />
            </Authentication>
          )}
        />
        <Route
          path="/bulk-request/:bulkRequestId"
          render={() => (
            <Authentication>
              <BulkRequestPage />
            </Authentication>
          )}
        />
        <Route
          path="/bulk-requests"
          render={(props) => (
            <MainLayout>
              <BulkRequestsPage {...props} />
            </MainLayout>
          )}
        />
        <Route
          path="/document"
          render={() => (
            <Switch>
              <Redirect exact path="/document" to="/documents" />
              <Route path="/document/aborted">
                <DocumentLayout>
                  <AbortedPage />
                </DocumentLayout>
              </Route>
              <Route path="/document/audit-trail">
                <DocumentLayout>
                  <AuditTrailPage />
                </DocumentLayout>
              </Route>
              <Route path="/document/completed">
                <DocumentLayout>
                  <CompletedPage />
                </DocumentLayout>
              </Route>
              <Route path="/document/on-signing">
                <DocumentLayout>
                  <OnSigningPage />
                </DocumentLayout>
              </Route>
              <Route path="/document/wait-for-signing">
                <DocumentLayout>
                  <WaitForSigningPage />
                </DocumentLayout>
              </Route>
              <Route path="/document/scheduled">
                <DocumentLayout>
                  <ScheduledDocumentPage />
                </DocumentLayout>
              </Route>
              {/* /document/:documentId path를 가지는 Route가 가장 하위에 선언돼야합니다. */}
              <Route component={DocumentIdPage} path="/document/:documentId" />
            </Switch>
          )}
        />
        <Route component={DocumentBundleDownloadPage} path="/document-bundle/download" />
        <Route
          path="/document-download/:id"
          render={(props) => (
            <Authentication>
              <DocumentDownloadPage {...props} />
            </Authentication>
          )}
        />
        <Route path="/documents">
          <Switch>
            <Route path="/documents/sample/:documentId">
              <Authentication>
                <DocumentDetailSamplePage />
              </Authentication>
            </Route>
            <Route path="/documents/:documentId">
              <Authentication>
                <DocumentDetailPage />
              </Authentication>
            </Route>
            <Route path="/documents">
              <MainLayout>
                <DocumentsPage />
              </MainLayout>
            </Route>
          </Switch>
        </Route>
        <Route path="/external-documents/(list|document)">
          <MainLayout>
            <Switch>
              <Route path="/external-documents/document/:documentId">
                <ExternalDocumentsDocumentDetailPage />
              </Route>
              <Route path="/external-documents/list">
                <ExternalDocumentsDocumentListPage />
              </Route>
            </Switch>
          </MainLayout>
        </Route>
        <Route path="/external-documents/batch-update">
          <Authentication>
            <ExternalDocumentsBatchUpdateHistoryPage />
          </Authentication>
        </Route>
        <Route path="/external-documents/upload">
          <Authentication>
            <ExternalDocumentsUploadPage />
          </Authentication>
        </Route>
        <Route path="/external-documents/file-viewer/:documentId">
          <Authentication>
            <ExternalDocumentsFileViewerPage />
          </Authentication>
        </Route>
        <Route path="/draft">
          <Switch>
            <Route component={DraftTempPage} path="/draft/temp" />
            <Route component={DraftDraftIdPage} path="/draft/:draftId" />
          </Switch>
        </Route>
        <Route
          path="/editing-documents"
          render={(props) => (
            <MainLayout>
              <EditingDocumentsPage {...props} />
            </MainLayout>
          )}
        />
        <Route path="/editor">
          <EditorLayout>
            <Switch>
              <Redirect exact path="/editor" to="/documents" />
              <Route component={EditorOthersRequireRoute} path="/editor/others-require" />
              <Route component={EditorSinglePage} path="/editor/single" />
              {/**
               * 예전에 서명 링크 주소가 /editor/validation/:token 였는데 변경되서
               * 하위 호환을 위해 token 기준으로 링크 정보 조회 후 /participant에
               * di, pi, lk, smt query string 붙여서 리다이렉트 하기 위한 라우트 페이지입니다.
               * @see: https://modusign.slack.com/archives/C01PP3J0U3H/p1622775710061600
               */}
              <Route component={EditorValidationPage} path="/editor/validation/:token" />
            </Switch>
          </EditorLayout>
        </Route>
        <Route
          path="/export-input/values/:jobId/download"
          render={(props) => (
            <Authentication>
              <ExportInputValuesDownloadPage {...props} />
            </Authentication>
          )}
        />
        <Route
          path="/drafts"
          render={(props) => (
            <MainLayout>
              <DraftsPage {...props} />
            </MainLayout>
          )}
        />
        <Route component={DraftPreviewPage} path="/draft-preview/:draftId" />
        <Route component={FormAgreementPage} path="/form-agreement" />
        <Route component={FormPreviewPage} path="/form-preview/:formId" />
        <Route
          path="/links"
          render={(props) => (
            <MainLayout>
              <LinksPage {...props} />
            </MainLayout>
          )}
        />
        <Route path="/link/create">
          <ContractLinkCreateLayout>
            <Switch>
              <Redirect exact path="/link/create" to="/documents" />
              <Route component={LinksCreatePositionPage} path="/link/create/position" />
              <Route component={LinksCreateEtcPage} path="/link/create/etc" />
            </Switch>
          </ContractLinkCreateLayout>
        </Route>
        <Route path="/link/:linkId">
          <ExitPathContextProvider>
            <Switch>
              <Route component={LinksAuthenticationPage} path="/link/:linkId/authentication" />
              <Route component={LinksTemplatePage} path="/link/:linkId/template" />
              <Route component={LinksLinkIdPage} path="/link/:linkId" />
            </Switch>
          </ExitPathContextProvider>
        </Route>
        <Route component={ApprovalDocumentPreview} path="/approvals/:approvalId/preview" />
        <Route path="/approvals">
          <MainLayout>
            <Switch>
              <Route component={ApprovalsEditPage} path="/approvals/:approvalId/edit" />
              <Redirect exact path="/approvals/:approvalId" to="/approvals/:approvalId/steps" />
              <Route component={ApprovalsApprovalIdPage} path="/approvals/:approvalId/:section" />
              <Route component={ApprovalListPage} exact path="/approvals" />
              <Redirect to="/approvals" />
            </Switch>
          </MainLayout>
        </Route>
        <Route
          path="/participant"
          render={(props) => (
            <ParticipantLayout>
              <Switch>
                <Route component={ParticipantPage} exact path="/participant" {...props} />
                <Route
                  component={AccessWithNamePage}
                  path="/participant/access-with-name"
                  {...props}
                />
              </Switch>
            </ParticipantLayout>
          )}
        />
        <Route
          path="/password"
          render={(props) => (
            <PasswordLayout {...props}>
              <Switch>
                <Redirect exact path="/password" to="/documents" />
                <Route component={ForgotPage} path="/password/forgot" />
                <Route component={ResetInvalidPage} path="/password/reset/invalid" />
                <Route component={ResetPage} path="/password/reset/:token" />
              </Switch>
            </PasswordLayout>
          )}
        />
        <Route
          path="/payment"
          render={(props) => (
            <PaymentLayout {...props}>
              <Switch>
                <Redirect exact path="/payment" to="/payment/subscription" />
                <Route component={SubscriptionPage} path="/payment/subscription" />
                <Route component={ProductPage} path="/payment/product" />
                <Route component={PlanChangePage} path="/payment/plan-change" />
                <Route component={ReadySubscriptionPage} path="/payment/ready-subscription" />
                <Route component={SeatPage} path="/payment/seat" />
                <Route
                  component={VirtualAccountCreateSuccessPage}
                  path="/payment/virtual-account-create-success"
                />
              </Switch>
            </PaymentLayout>
          )}
        />
        <Route
          path="/settings/address-book/excel-upload"
          render={(props) => (
            <Authentication>
              <AddressBookExcelUploadPage {...props} />
            </Authentication>
          )}
        />
        <Route
          path="/settings"
          render={() => (
            <MainLayout>
              <SettingLayout>
                <Switch>
                  <Redirect exact path="/settings" to="/settings/profile" />
                  <Route component={AddressBookPage} path="/settings/address-book" />
                  <Route component={AlarmPage} path="/settings/alarm" />
                  <Route component={ApiPage} path="/settings/api" />
                  <Route component={ApiKeyPage} path="/settings/api-key/:id" />
                  <Route component={ApplicationPage} path="/settings/applications/:applicationId" />
                  <Route component={ApplicationsPage} path="/settings/applications" />
                  <Route component={BillingPage} path="/settings/billing" />
                  <Route component={CustomBrandingPage} path="/settings/custom-branding" />
                  <Route component={WorkspaceMembersPage} path={settingsWorkspaceRoutes.members} />
                  <Route
                    component={WorkspaceGroupDetailPage}
                    path={settingsWorkspaceRoutes.groupDetail}
                  />
                  <Route component={WorkspaceGroupsPage} path={settingsWorkspaceRoutes.groups} />
                  <Route
                    component={WorkspaceMemberInvitationPage}
                    path={settingsWorkspaceRoutes.memberInvitation}
                  />
                  <Route
                    component={WorkspaceAutomaticSharingPage}
                    path={settingsWorkspaceRoutes.automaticSharing}
                  />
                  <Route component={PlanPage} path="/settings/plan" />
                  <Route component={ProfilePage} path="/settings/profile" />
                  <Route component={WebhookPage} path="/settings/webhook/:id" />
                  <Route component={ExternalServicePage} path="/settings/external-service" />
                  <Switch>
                    <Redirect exact path="/settings" to={settingsWorkspaceRoutes.workspace} />
                    <Route
                      component={WorkspaceSettingPage}
                      path={settingsWorkspaceRoutes.setting}
                    />
                    <Route
                      component={ChangeWorkspaceOwnerPage}
                      path="/settings/workspace/change-owner"
                    />
                    <Route component={DeleteWorkspacePage} path="/settings/workspace/delete" />
                    <Route component={WorkspaceRolePage} path={settingsWorkspaceRoutes.role} />
                    <Route
                      component={WorkspaceRoleFormPage}
                      path={settingsWorkspaceRoutes.roleForm}
                    />
                    <Route component={WorkspacePage} path={settingsWorkspaceRoutes.workspace} />
                  </Switch>
                </Switch>
              </SettingLayout>
            </MainLayout>
          )}
        />
        <Route
          path="/file-hash"
          render={() => (
            <FileHashPageLayout>
              <Switch>
                <Route component={FileHashVerificationPage} path="/file-hash/verification" />
                <Redirect path="/file-hash" to="/file-hash/verification" />
              </Switch>
            </FileHashPageLayout>
          )}
        />
        <Route
          path="/signatures"
          render={(props) => (
            <MainLayout>
              <SignaturesPage {...props} />
            </MainLayout>
          )}
        />
        <Route
          path="/in-person"
          render={() => (
            <Switch>
              <Route component={InPersonHostPage} path="/in-person/host" />
              <Route component={InPersonSignerPage} path="/in-person/signer" />
              <Route component={InPersonWaitForSigning} path="/in-person/wait-for-signing" />
              <Redirect path="/in-person" to="/documents" />
            </Switch>
          )}
        />
        <Route
          path="/signing"
          render={() => (
            <SigningLayout>
              <Switch>
                <Redirect exact path="/signing" to="/documents" />
                <Route component={SigningAuthenticationPage} path="/signing/authentication" />
                <Route component={SigningCompletePage} path="/signing/complete" />
                <Route component={SigningExpiredPage} path="/signing/expired" />
                <Route component={ProgressPage} path="/signing/progress" />
              </Switch>
            </SigningLayout>
          )}
        />
        <Route
          path="/template/setup/"
          render={(props) => (
            <TemplateCreationLayout {...props}>
              <Switch>
                <Route
                  component={TemplateSetupParticipantSetupPage}
                  path="/template/setup/participant-setup"
                />
                <Route component={TemplateSetupFieldSetupPage} path="/template/setup/field-setup" />
                <Route
                  component={TemplateSetupGeneralSetupPage}
                  path="/template/setup/general-setup"
                />
              </Switch>
            </TemplateCreationLayout>
          )}
        />
        <Route
          path="/template/:id/edit/"
          render={(props) => (
            <TemplateEditionLayout {...props}>
              <Switch>
                <Route
                  component={TemplateEditParticipantEditPage}
                  path="/template/:id/edit/participant-edit"
                />
                <Route component={TemplateEditFieldEditPage} path="/template/:id/edit/field-edit" />
                <Route
                  component={TemplateEditGeneralEditPage}
                  path="/template/:id/edit/general-edit"
                />
              </Switch>
            </TemplateEditionLayout>
          )}
        />
        <Route
          path="/templates"
          render={(props) => (
            <MainLayout>
              <TemplatesPage {...props} />
            </MainLayout>
          )}
        />
        <Route path="/reminders-sample">
          <Authentication>
            <ReminderListSamplePage />
          </Authentication>
        </Route>

        <Route path="/reminders">
          <Authentication>
            <ReminderListPage />
          </Authentication>
        </Route>
        <Route component={ViewerPage} path="/viewer/:token" />
        <Route
          path="/oauth"
          render={() => (
            <Switch>
              <Route component={ExternalServiceConnectionErrorPage} path="/oauth/error" />
              {/** FIXME: Authentication이랑 조화롭게 동작하도록 개선 필요합니다.
               * @see https://modusign.slack.com/archives/CBT88M56C/p1691039817864839
               */}
              <Route component={RedirectAuthorizationPage} path="/oauth/authorize" />
              <Authentication>
                <Route component={PermissionAuthorizationPage} path="/oauth/approve" />
              </Authentication>
            </Switch>
          )}
        />
        <Route component={NotFoundPage} />
      </Switch>
    </RootLayout>
  );
}

function EmbeddedRoutes() {
  return (
    <RootLayout>
      <Switch>
        <Route component={EmbeddedDocumentPage} exact path="/embedded-document/:documentId" />
        <Route component={EmbeddedDraftPage} exact path="/embedded-draft/:draftId" />
        <Route component={EmbeddedParticipantPage} exact path="/embedded-participant" />
        <Route component={EmbeddedTemplatePage} exact path="/embedded-template/:templateId" />
        <Route component={EmbeddedDraftPreviewPage} exact path="/draft/preview" />
        <Route path="/editor/others-require">
          <EditorLayout>
            <EditorOthersRequireRoute />
          </EditorLayout>
        </Route>
        <Route
          path="/document"
          render={() => (
            <DocumentLayout>
              <Switch>
                <Route component={AbortedPage} path="/document/aborted" />
                <Route component={AuditTrailPage} path="/document/audit-trail" />
                <Route component={CompletedPage} path="/document/completed" />
                <Route component={OnSigningPage} path="/document/on-signing" />
                <Route component={DocumentViewerPage} path="/document/viewer" />
                <Route component={WaitForSigningPage} path="/document/wait-for-signing" />
                {/* /document/:documentId path를 가지는 Route가 가장 하위에 선언돼야합니다. */}
                <Route component={DocumentIdPage} path="/document/:documentId" />
              </Switch>
            </DocumentLayout>
          )}
        />
        <Route
          path="/signing"
          render={() => (
            <SigningLayout>
              <Switch>
                <Redirect exact path="/signing" to="/documents" />
                <Route component={SigningAuthenticationPage} path="/signing/authentication" />
                <Route component={SigningCompletePage} path="/signing/complete" />
                <Route component={SigningExpiredPage} path="/signing/expired" />
                <Route component={ProgressPage} path="/signing/progress" />
              </Switch>
            </SigningLayout>
          )}
        />
        <Route
          path="/template/setup/"
          render={(props) => (
            <TemplateCreationLayout {...props}>
              <Switch>
                <Route
                  component={TemplateSetupParticipantSetupPage}
                  path="/template/setup/participant-setup"
                />
                <Route component={TemplateSetupFieldSetupPage} path="/template/setup/field-setup" />
                <Route
                  component={TemplateSetupGeneralSetupPage}
                  path="/template/setup/general-setup"
                />
              </Switch>
            </TemplateCreationLayout>
          )}
        />
        <Route
          path="/template/:id/edit/"
          render={(props) => (
            <TemplateEditionLayout {...props}>
              <Switch>
                <Route
                  component={TemplateEditParticipantEditPage}
                  path="/template/:id/edit/participant-edit"
                />
                <Route component={TemplateEditFieldEditPage} path="/template/:id/edit/field-edit" />
                <Route
                  component={TemplateEditGeneralEditPage}
                  path="/template/:id/edit/general-edit"
                />
              </Switch>
            </TemplateEditionLayout>
          )}
        />
        <Route
          path="/in-person"
          render={() => (
            <Switch>
              <Route component={InPersonHostPage} path="/in-person/host" />
              <Route component={InPersonSignerPage} path="/in-person/signer" />
              <Route component={InPersonWaitForSigning} path="/in-person/wait-for-signing" />
              <Redirect path="/in-person" to="/documents" />
            </Switch>
          )}
        />
        <Route component={NotFoundPage} />
      </Switch>
    </RootLayout>
  );
}

function Routes() {
  useDataLayer();
  useOnboardingGuide();

  if (embeddedService.isEmbedded) {
    return <EmbeddedRoutes />;
  }

  return <AppRoutes />;
}

export default Routes;
